import { useEffect, useState } from 'react';

import { optionsRevealAtMs } from '../api/models';
import { AppText } from '../components/AppText';
import { InlineNotice } from '../components/InlineNotice';
import type { NoticeTone } from '../components/InlineNotice';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import type { TaskState } from '../session/sessionState';
import { acceptsInput, canSubmit } from '../session/sessionState';
import { Strings } from '../strings';

import {
  GridSelectionAnswer,
  MultipleChoiceAnswer,
  OrderingAnswer,
  TextAnswerControl,
} from './AnswerControls';
import { StimulusView } from './StimulusView';

/**
 * One task, start to finish — design-system/07-Challenges/01-Challenge-Shell.md.
 * Mirrors android/…/Challenges/ChallengeShell.kt.
 *
 * The shell owns the frame: progress, instruction, stimulus, answer control, feedback, action. The two
 * switches inside it are independent and both come from the instance, never from the catalogue: the
 * presentation chooses the answer control, the stimulus kind chooses what is shown (manifest §6a).
 *
 * There is no timer anywhere on this screen, and no state paints anything red — the two rules a training
 * screen exists to keep (BR-12, BR-13).
 */
export function ChallengeShell({
  task,
  position,
  total,
  isSubmitting,
  hint,
  hintsRemaining,
  submitError = null,
  onSelect,
  onType,
  onReorder,
  onSubmit,
  onRequestHint,
  onNext,
}: {
  task: TaskState;
  position: number;
  total: number;
  isSubmitting: boolean;
  /** A hint the child asked for. Shown in the same slot as a wrong answer's hint (R0076). */
  hint: string | null;
  hintsRemaining: number;
  /**
   * A failure while answering. Shown **beside** the task, never instead of it: a submit failure used to
   * replace the whole screen, so a memorised pattern the child could not get back was thrown away
   * (R0116).
   */
  submitError?: string | null;
  onSelect: (key: string) => void;
  onType: (text: string) => void;
  onReorder: (ordering: string[]) => void;
  onSubmit: () => void;
  onRequestHint: () => void;
  onNext: () => void;
}) {
  const item = task.item;
  const options = item.options ?? [];
  // A story is read before it is asked about; anything memorised is asked about first.
  const readingFirst = item.stimulus.kind === 'text' && (item.stimulus.text ?? '').length > 0;
  const areOptionsVisible = useMoment(item.instanceToken, optionsRevealAtMs(item.stimulus));
  // The **stimulus** going, which is 2.5 s before the options arrive (R0092). The question is due at the
  // first of those moments, not the second: the pattern is gone, so „Zapamätaj si čísla." has stopped
  // being true, and a child staring at an empty box for two and a half seconds is not told what to do
  // with what they are holding (R0126).
  const isStimulusHidden = useMoment(item.instanceToken, item.stimulus.exposureMs ?? 0);
  const inputEnabled = acceptsInput(task) && !isSubmitting && areOptionsVisible;

  // A timed task has two moments and two sentences: what to do while the stimulus is up, and what to
  // answer once it has gone. The question used to be on screen the whole time, so a child was reading it
  // instead of memorising — and the digits did not wait (R0126).
  const heading =
    !isStimulusHidden && item.memoriseInstruction !== null ? item.memoriseInstruction : item.instruction;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 'var(--column)',
        margin: '0 auto',
        padding: 'var(--space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-5)',
      }}
    >
      <ProgressRail position={position} total={total} />

      <AppText style="bodyS" color="var(--text-secondary)">
        {Strings.sessionProgress(position, total)}
      </AppText>

      {/*
        Which comes first depends on what the stimulus is (R0106).

        A **story** is read and then asked about — nobody answers a question about a text they have not
        read. A **pattern the child must hold in memory** is the other way round: 0017 asked for the
        question first, because seeing the numbers and the question together defeats the remembering.
      */}
      {readingFirst ? (
        <>
          <StimulusView stimulus={item.stimulus} />

          <AppText style="titleS" as="h1">
            {heading}
          </AppText>
        </>
      ) : (
        <>
          <AppText style="titleS" as="h1">
            {heading}
          </AppText>

          <StimulusView stimulus={item.stimulus} />
        </>
      )}

      {/*
        „Zapamätaj si to…" belongs only to a task that has not said what to remember. A digit task now says
        „Zapamätaj si čísla." in the heading itself, and two sentences meaning the same thing, one above
        the other, is one too many (R0126).
      */}
      {!areOptionsVisible && item.memoriseInstruction === null ? (
        <AppText style="bodyL" color="var(--text-secondary)">
          {Strings.sessionMemorise}
        </AppText>
      ) : null}

      {areOptionsVisible
        ? (() => {
            switch (item.presentation) {
              case 'multiple_choice':
                return (
                  <MultipleChoiceAnswer
                    options={options}
                    selectedKeys={task.selectedOptionKeys}
                    isEnabled={inputEnabled}
                    onSelect={onSelect}
                  />
                );
              case 'grid_selection':
                return (
                  <GridSelectionAnswer
                    options={options}
                    selectedKeys={task.selectedOptionKeys}
                    isEnabled={inputEnabled}
                    onSelect={onSelect}
                  />
                );
              case 'text_answer':
                return (
                  <TextAnswerControl value={task.typedText} isEnabled={inputEnabled} onChange={onType} />
                );
              case 'ordering':
              case 'sequence_recall':
                return (
                  <OrderingAnswer
                    options={options}
                    ordering={task.ordering}
                    isEnabled={inputEnabled}
                    onChange={onReorder}
                  />
                );
              default:
                // A presentation this build does not know. The child sees the instruction and a plain
                // sentence, never a blank screen — and the task is still submittable, so the session can
                // continue.
                reportUnknownPresentation(item.presentation);

                return <InlineNotice text={Strings.sessionUnsupported} tone="info" />;
            }
          })()
        : null}

      {/*
        The task stays. `info`, never `error` — there is no red on a child's screen (§4), and a network
        failure is not their fault. „Skontrolovať" below is still live, so trying again re-sends the same
        answer rather than reloading the session.
      */}
      {submitError !== null ? <InlineNotice text={submitError} tone="info" /> : null}

      {/* One slot for help, whether the child asked or answered wrongly (R0076). */}
      {task.feedbackText !== null ? (
        <InlineNotice text={task.feedbackText} tone={toneFor(task.feedbackTone)} />
      ) : hint !== null ? (
        <InlineNotice text={hint} tone="hint" />
      ) : null}

      {task.phase === 'solved' || task.phase === 'closed' ? (
        <PrimaryButton label={Strings.sessionNext} onClick={onNext} />
      ) : (
        <>
          {/* Secondary: asking for help is an alternative, not the point of the screen. */}
          {hintsRemaining > 0 ? (
            <SecondaryButton label={Strings.sessionHint} onClick={onRequestHint} />
          ) : null}
          <PrimaryButton
            label={Strings.sessionCheck}
            onClick={onSubmit}
            enabled={canSubmit(task)}
            loading={isSubmitting}
          />
        </>
      )}
    </div>
  );
}

/** FeedbackTone has no error value, so this cannot produce one (BR-13). */
const toneFor = (tone: TaskState['feedbackTone']): NoticeTone => (tone === 'hint' ? 'hint' : 'info');

const reportedPresentations = new Set<string>();

function reportUnknownPresentation(presentation: string): void {
  if (reportedPresentations.has(presentation)) return;

  reportedPresentations.add(presentation);
  // eslint-disable-next-line no-console
  console.warn(`[challenges] Unknown presentation: ${presentation}`);
}

/**
 * How far through the session the child is.
 *
 * A count of tasks, never a clock. It grows by a segment when a replacement task arrives, which is honest:
 * there genuinely is one more task to do.
 */
function ProgressRail({ position, total }: { position: number; total: number }) {
  return (
    <div
      role="progressbar"
      aria-valuenow={position}
      aria-valuemin={0}
      aria-valuemax={total}
      style={{ display: 'flex', gap: 'var(--space-1)', width: '100%', height: 8 }}
    >
      {Array.from({ length: total }, (_unused, index: number) => (
        <span
          key={index}
          style={{
            flex: 1,
            height: 8,
            borderRadius: 'var(--radius-pill)',
            background: index < position ? 'var(--progress-fill)' : 'var(--progress-track)',
          }}
        />
      ))}
    </div>
  );
}

/**
 * Whether a moment of a timed task has arrived yet — the stimulus going, or the options coming.
 *
 * `true` immediately for everything that is not memorised, so only recall tasks wait. Keyed on the
 * instance token so moving to the next task restarts the wait rather than inheriting the last one's —
 * the same class of leak that left the hint button retired for a whole session in R0076.
 */
function useMoment(instanceToken: string, atMs: number): boolean {
  const [hasArrived, setHasArrived] = useState(atMs <= 0);

  useEffect(() => {
    if (atMs <= 0) {
      setHasArrived(true);

      return;
    }

    setHasArrived(false);

    const handle = window.setTimeout(() => setHasArrived(true), atMs);

    return () => window.clearTimeout(handle);
  }, [instanceToken, atMs]);

  return hasArrived;
}
